/* =========================================================================
   VD CART DRAWER — unified build (single theme, A/B via code branch)
   Load globally:  layout/theme.liquid, before </body>:
     <script src="{{ 'vd-cart-drawer.js' | asset_url }}" defer></script>

   2026-08-07 — merged from live production-v7 (t/116) + live TESTING (t/113).

   WHAT CHANGED vs the two live files
   ----------------------------------
   [A/B]  One file now serves BOTH arms. <cart-drawer data-cd-variant="control|gift">
          decides which code path runs. Resolved ONCE at init and frozen, so a late
          Intelligems write can never flip a shopper mid-session.
            control = today's live V7 behaviour (single-tier band, list dropdown,
                      Shopify recommendations)
            gift    = today's TESTING behaviour (gift tier, swatch popover,
                      gap-closer sale rail)
          Variant is a CODE branch, not a DOM patch, because refresh()/refreshChrome()
          re-render and replaceWith() the drawer chrome on every cart write — any
          visual-editor style A/B tool would be wiped out here.

   [P0-1] money() no longer hardcodes '$' — Intl.NumberFormat + Shopify.currency.active.
   [P0-2] api() now checks response.ok and surfaces Shopify's 422 description.
   [P0-3] All cart writes go through a single-flight queue (no more click races).
   [P0-4] swap() is one atomic /cart/update.js call (was add.js -> change.js).
   [P0-5] Gift guard is driven by Shopify's own discount result (final_line_price),
          not by a threshold the theme computes itself, and removes ALL gift lines.

   [+]    vd:cart:* CustomEvents on every meaningful interaction (was zero telemetry).
   [+]    Shopify.routes.root instead of hardcoded /cart/*.js paths.
   [+]    refreshChrome() uses cache:'no-store' and checks .ok.

   [2026-10-02] Rail quick-add redesigned (Hailin, Figma 602:4013 → section 1097:18):
          card = image + title + price + one "+ Add" (swatch dots removed); the whole card
          opens a quick-add SHEET inside the panel (colour siblings + sizes, no pre-selection,
          SELECT SIZE → ADD TO BAG gating, scrim/Esc close only, no ×). One-size products with
          no siblings add directly. Events: rec_sheet_open · rec_colour_switch · rec_size_select · rec_add{via}.
   REQUIRES in sections/cart-drawer.liquid — see _patched/README-cart-drawer.md
   ========================================================================= */
(function () {

  // Display title (2026-08-21): strip the "(PRE-ORDER)" prefix everywhere a line-item
  // title is shown — the PDP/card badge owns that message.
  function dTitle(t) { return (t || '').replace(/^\s*\(PRE-?ORDER\)\s*/i, ''); }
  var MAX_SW = 5, MAX_REC = 8;
  var pcache = {}, saleCache = {};
  var recSig = -1, recCache = '';
  var giftBusy = false, giftNoticePending = null;
  var lastLive = '';
  var lastGiftUnlocked = null;   // telemetry: previous unlock state, to emit transitions only
  var drawerEverOpened = false;  // once true the A/B arm is frozen for the session

  /* ==================================================================
     0. VARIANT GATE
     ================================================================== */
  var VARIANT = null;
  function drawerEl() { return document.querySelector('[data-vd-cart-drawer]'); }

  // Resolve once, then freeze. Order of precedence:
  //   1. window.VD_CD_VARIANT        (set by Intelligems JS API before this script runs)
  //   2. data-cd-variant attribute   (set by Liquid, or by IG's attribute injection)
  //   3. inferred from data-gift     (keeps today's live behaviour when no test is running)
  function resolveVariant() {
    var d = drawerEl();
    var v = String(window.VD_CD_VARIANT || (d && d.getAttribute('data-cd-variant')) || '').toLowerCase();
    if (v !== 'control' && v !== 'gift') v = (d && d.getAttribute('data-gift')) ? 'gift' : 'control';
    VARIANT = v;
    if (d) d.setAttribute('data-cd-variant', v);       // reflect it back for CSS + QA
    return v;
  }
  function variant() { return VARIANT || resolveVariant(); }
  function isGift() { return variant() === 'gift'; }

  /* ==================================================================
     1. TELEMETRY  — vd:cart:*
     Every handler below emits one. A Shopify Web Pixel (or GTM) listens on
     document and forwards to Intelligems / GA4. Without this the primary
     metric (cart -> checkout) cannot be computed at all.
     ================================================================== */
  function emit(name, detail) {
    detail = detail || {};
    detail.variant = variant();
    detail.ts = Date.now();
    // 1. document CustomEvent — for anything running in the page (Intelligems, GTM in-page)
    try { document.dispatchEvent(new CustomEvent('vd:cart:' + name, { detail: detail, bubbles: true })); } catch (e) {}
    // 2. dataLayer — for GTM
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'vd_cart_' + name }, detail)); } catch (e) {}
    // 3. Shopify analytics bus — REQUIRED for Web Pixels.
    //    Custom Web Pixels run in a sandboxed iframe with NO access to this document, so a
    //    pixel can never see the CustomEvent above. Shopify.analytics.publish is the only
    //    supported bridge; the pixel subscribes with analytics.subscribe('vd_cart_<name>').
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('vd_cart_' + name, detail);
      }
    } catch (e) {}
    // 4. Intelligems — THE primary channel for this A/B.
    //    IG runs IN THE PAGE, so it reads this queue directly. It cannot see a Web Pixel
    //    (sandboxed iframe), and it needs no HTTP endpoint. The queue is safe to push to
    //    before IG loads: it drains window.igEvents on init.
    //    Each name must be registered once in IG > Events Manager before it can be used
    //    as a metric in an experiment. Identifiers are capped at 100 chars.
    try {
      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: 'vd_cart_' + name, variant: detail.variant });
    } catch (e) {}
  }

  /* ==================================================================
     2. SHARED INFRASTRUCTURE  (routes / currency / write queue / errors)
     ================================================================== */
  var R = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // ---- P0-1: real currency, not a hardcoded dollar sign -------------
  // Verified on this store: Shopify.currency = {active:"USD"}, Shopify.locale = "en".
  // Shopify.formatMoney does NOT exist here (custom theme, Dawn's global.js is not
  // loaded), so Intl.NumberFormat is the correct tool.
  var VD_CUR = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  var VD_LOC = (window.Shopify && window.Shopify.locale) || 'en';
  var VD_FMT = null;
  try {
    VD_FMT = new Intl.NumberFormat(VD_LOC, {
      style: 'currency', currency: VD_CUR,
      minimumFractionDigits: 0, maximumFractionDigits: 2
    });
  } catch (e) {}
  function money(c) {
    var d = (c || 0) / 100;
    if (VD_FMT) return VD_FMT.format(d);
    return VD_CUR + ' ' + (d % 1 === 0 ? d : d.toFixed(2));
  }

  // ---- P0-3: single-flight write queue ------------------------------
  // Only one cart mutation is ever in flight. Responses can no longer land out of
  // order, so refresh() can no longer paint a stale cart.
  var cartQ = Promise.resolve(), pending = 0;
  function setWriting(on) {
    var d = drawerEl() || document.querySelector('cart-drawer');
    if (d) d.classList.toggle('vd-cd-writing', on);
  }
  function enqueue(task) {
    pending++; setWriting(true);
    var p = cartQ.then(task, task);          // a failed predecessor must not stall the queue
    cartQ = p.catch(function () {});
    p.catch(function () {}).then(function () { if (--pending === 0) setWriting(false); });
    return p;
  }

  // ---- P0-2 companion: visible error feedback -----------------------
  function cartErr(e) {
    return (e && (e.description || e.message)) || 'Something went wrong — please try again';
  }
  function showCartError(msg) {
    announce(msg);
    var host = document.querySelector('cart-drawer-items');
    if (!host || !host.parentNode) return;
    var el = document.querySelector('.vd-cd-err');
    if (!el) {
      el = document.createElement('p');
      el.className = 'vd-cd-err';
      el.setAttribute('role', 'status');
      host.parentNode.insertBefore(el, host);
    }
    el.textContent = msg;
    clearTimeout(el.__t);
    el.__t = setTimeout(function () { if (el.parentNode) el.remove(); }, 5000);
  }

  // a11y: polite live region in sections/cart-drawer.liquid ([data-cart-live]).
  // Clear-then-set so an identical repeated string still re-announces.
  function announce(msg) {
    var r = document.querySelector('[data-cart-live]'); if (!r) return;
    r.textContent = '';
    setTimeout(function () { r.textContent = msg; }, 60);
  }

  function injectCSS() {
    if (document.getElementById('vd-cd-css')) return;
    var css =
      '.vd-cd{font-family:var(--vd-font-body),Helvetica,Arial,sans-serif;color:#161616;padding:2px 13px 0;}' +
      '.vd-cd-row{display:flex;gap:13px;padding:15px 0;border-bottom:1px solid #EFEDE8;position:relative;}' +
      '.vd-cd-row__img{width:72px;height:72px;flex:0 0 auto;border-radius:3px;overflow:hidden;background:#F1F1EF;display:block;}' +
      '.vd-cd-row__img img{width:100%;height:100%;object-fit:cover;display:block;}' +
      '.vd-cd-row__img--btm img{transform:scale(1.6);transform-origin:50% 100%;}' +
      '.vd-cd-bd{flex:1;min-width:0;display:flex;flex-direction:column;gap:9px;}' +
      '.vd-cd-tp{display:flex;justify-content:space-between;gap:10px;}' +
      '.vd-cd-ti{font-size:12px;font-weight:800;text-transform:uppercase;line-height:1.3;letter-spacing:.01em;}' +
      '.vd-cd-pr{font-size:13px;font-weight:800;white-space:nowrap;}' +
      '.vd-cd-pr s{font-size:11px;color:#A8A69E;font-weight:400;margin-right:5px;}' +
      '.vd-cd-op{display:flex;align-items:center;gap:7px;flex-wrap:wrap;position:relative;}' +
      'button.vd-cd-op{background:none;border:none;padding:0;font:inherit;text-align:left;cursor:pointer;color:inherit;}' +
      '.vd-cd-lb{font-size:10px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#8C8A85;}' +
      '.vd-cd-sw{width:21px;height:21px;border-radius:50%;overflow:hidden;border:1px solid #e0e0e0;display:inline-block;flex:0 0 auto;cursor:pointer;padding:0;background:#f4f4f4;}' +
      '.vd-cd-sw img{width:100%;height:100%;object-fit:cover;transform:scale(1.5);display:block;}' +
      '.vd-cd-sw--btm img{object-position:50% 100%;transform-origin:50% 100%;transform:scale(1.9);}' +
      '.vd-cd-sw.is-cur{outline:1.5px solid #161616;outline-offset:1.5px;}' +
      '.vd-cd-mo{font-size:11px;font-weight:500;color:#A8A69E;background:none;border:none;cursor:pointer;padding:0;}' +
      '.vd-cd-cv{display:inline-flex;font-size:13px;color:#A8A69E;}' +
      '.vd-cd-sz{display:inline-flex;align-items:center;gap:6px;border:1px solid #E5E3DD;background:#fff;border-radius:4px;padding:5px 10px;font:800 10px/1 var(--vd-font-body),Helvetica,Arial,sans-serif;text-transform:uppercase;cursor:pointer;color:#161616;}' +
      '.vd-cd-qt{display:inline-flex;align-items:center;border:1px solid #E5E3DD;border-radius:4px;overflow:hidden;}' +
      '.vd-cd-qt button{width:28px;height:28px;border:none;background:#fff;font-size:15px;cursor:pointer;color:#161616;line-height:1;}' +
      '.vd-cd-qt button:disabled{opacity:.28;cursor:default;pointer-events:none;}' +
      '.vd-cd-qt span{width:28px;text-align:center;font-size:12px;font-weight:700;}' +
      '.vd-cd-rm{margin-left:auto;font:500 11px/1 var(--vd-font-body),Helvetica,Arial,sans-serif;text-decoration:underline;color:#A8A69E;background:none;border:none;cursor:pointer;}' +
      '.vd-cd-dd{position:absolute;z-index:40;top:calc(100% + 4px);left:0;background:#fff;border:1px solid #E5E3DD;border-radius:4px;box-shadow:0 12px 26px rgba(0,0,0,.16);padding:5px;max-height:240px;overflow:auto;min-width:160px;}' +
      '.vd-cd-dd button{display:flex;align-items:center;gap:9px;width:100%;border:none;background:none;padding:7px 8px;font:700 11px/1.1 var(--vd-font-body),Helvetica,Arial,sans-serif;text-transform:uppercase;cursor:pointer;border-radius:3px;text-align:left;color:#161616;}' +
      '.vd-cd-dd button:hover{background:#F5F3EF;}' +
      '.vd-cd-dd .vd-cd-sw{cursor:default;}' +
      /* gift arm — enlarged swatch popover */
      '.vd-cd-pop{left:50%;transform:translateX(-50%);top:calc(100% - 12px);width:min(342px,100%);padding:14px 14px 16px;border-radius:6px;box-shadow:0 12px 26px rgba(0,0,0,.16);max-height:none;}' +
      '.vd-cd-pop-h{display:flex;gap:8px;align-items:center;margin-bottom:12px;font:700 10px/1 var(--vd-font-body),Helvetica,Arial,sans-serif;letter-spacing:.5px;text-transform:uppercase;}' +
      '.vd-cd-pop-l{color:#A8A69E;}.vd-cd-pop-n{color:#161616;}' +
      /* Dismissal model (Hailin 2026-08-12, FINAL): no explicit ×. The picker closes on any click
         outside it (that click is swallowed — see onDoc — so it can never ALSO swap a colour or
         follow a link underneath), on Escape, or on tapping the current colour (guard in
         commitColour). A × shipped briefly in both arms on 2026-08-12 and was rejected same day. */
      '.vd-cd-pop-g{display:flex;flex-wrap:wrap;gap:10px;}' +
      '.vd-cd-pop button.vd-cd-swl{display:block;width:44px;height:44px;border-radius:50%;border:none;background:none;padding:2px;position:relative;cursor:pointer;}' +
      '.vd-cd-pop button.vd-cd-swl:hover{background:#F5F3EF;}' +
      '.vd-cd-swl.is-cur{box-shadow:0 0 0 1.5px #161616;}' +
      '.vd-cd-swl-d{display:block;width:100%;height:100%;border-radius:50%;overflow:hidden;border:1px solid #e0e0e0;background:#f4f4f4;}' +
      '.vd-cd-swl-d img{width:100%;height:100%;object-fit:cover;transform:scale(1.5);display:block;}' +
      '.vd-cd-swl.is-so .vd-cd-swl-d{opacity:.35;}' +
      '.vd-cd-swl.is-so::after{content:"";position:absolute;left:7px;right:7px;top:50%;height:1.5px;background:#A8A69E;transform:rotate(-45deg);}' +
      '.vd-cd-rec{padding:2px 0 8px;}' +
      '.vd-cd-rec h4{font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;margin:14px 0 12px;color:#161616;}' +
      '.vd-cd-rr{display:flex;gap:10px;overflow-x:auto;padding-bottom:4px;scrollbar-width:none;}' +
      '.vd-cd-rr::-webkit-scrollbar{display:none;}' +
      /* card (Figma 651:77 / 1097:19, 2026-10-02): 116 wide, 1:1 image, 2-line title, price, ONE full-width action.
         No swatch dots. Whole card opens the quick-add sheet. */
      '.vd-cd-cc{flex:0 0 116px;display:flex;flex-direction:column;background:#fff;scroll-snap-align:start;padding:1px;}' +
      '.vd-cd-ci{display:block;aspect-ratio:1;background:#F1F1EF;overflow:hidden;}' +
      '.vd-cd-ci img{width:100%;height:100%;object-fit:cover;display:block;}' +
      '.vd-cd-cb{padding:8px 9px 10px;display:flex;flex-direction:column;gap:2px;flex:1;}' +
      '.vd-cd-ct{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;height:25px;font-size:10px;font-weight:700;line-height:1.25;text-transform:uppercase;color:#161616;text-decoration:none;}' +
      '.vd-cd-cp{display:flex;gap:5px;align-items:baseline;font-size:12px;font-weight:700;white-space:nowrap;margin-bottom:2px;}' +
      '.vd-cd-cp s{font-size:10px;color:#A8A69E;font-weight:400;}' +
      '.vd-cd-cp .vd-cd-now{color:#D9211C;}' +
      '.vd-cd-ad{margin-top:auto;display:inline-flex;align-items:center;justify-content:center;gap:4px;border:none;background:#161616;color:#fff;font:700 9px/1 var(--vd-font-body),Helvetica,Arial,sans-serif;letter-spacing:.6px;text-transform:uppercase;height:25px;padding:0 8px;border-radius:0;cursor:pointer;}' +
      '.vd-cd-ic{display:block;flex:0 0 auto;}' +
      '.vd-cd-ad.is-added{background:#fff;color:#161616;box-shadow:inset 0 0 0 1px #161616;}' +
      '.vd-cd-ad:disabled{opacity:.6;cursor:default;}' +
      '.vd-cd-ad.is-added:disabled{opacity:1;}' +
      '.vd-cd-cc a:focus-visible,.vd-cd-ad:focus-visible{outline:2px solid #161616;outline-offset:2px;}' +
      /* quick-add SHEET: inside the drawer panel, over the footer. Scrim tap / Esc = close only. */
      '.vd-cd-sheet-root{position:absolute;inset:0;z-index:6;}' +
      '.vd-cd-sheet-root[hidden]{display:none;}' +
      '.vd-cd-scrim{position:absolute;inset:0;background:rgba(22,22,22,.45);opacity:0;transition:opacity .22s;}' +
      '.vd-cd-sheet{position:absolute;left:0;right:0;bottom:0;max-height:88%;overflow-y:auto;background:#fff;padding:16px 16px calc(20px + env(safe-area-inset-bottom,0px));box-shadow:0 -6px 20px rgba(0,0,0,.14);transform:translateY(100%);transition:transform .28s cubic-bezier(.2,.7,.2,1);display:flex;flex-direction:column;gap:14px;outline:none;}' +
      '.vd-cd-sheet-root.is-in .vd-cd-scrim{opacity:1;}' +
      '.vd-cd-sheet-root.is-in .vd-cd-sheet{transform:none;}' +
      '@media (prefers-reduced-motion:reduce){.vd-cd-scrim,.vd-cd-sheet{transition:none;}}' +
      '.vd-cd-sh-hd{display:flex;gap:12px;align-items:flex-start;}' +
      '.vd-cd-sh-img{display:block;width:72px;height:90px;flex:0 0 auto;background:#F1F1EF;overflow:hidden;}' +
      '.vd-cd-sh-img img{width:100%;height:100%;object-fit:cover;display:block;}' +
      '.vd-cd-sh-meta{flex:1;min-width:0;display:flex;flex-direction:column;gap:5px;}' +
      '.vd-cd-sh-ti{font-size:12px;font-weight:700;text-transform:uppercase;line-height:1.2;color:#161616;}' +
      '.vd-cd-sh-pr{font-size:13px;font-weight:700;color:#161616;}' +
      '.vd-cd-sh-pr s{font-size:11px;color:#A8A69E;font-weight:400;margin-right:6px;}' +
      '.vd-cd-sh-pr .vd-cd-now{color:#D9211C;}' +
      '.vd-cd-sh-view{font-size:9px;font-weight:700;letter-spacing:.6px;text-transform:uppercase;color:#6A6A6A;text-decoration:underline;text-underline-offset:2px;align-self:flex-start;}' +
      '.vd-cd-sh-row{display:flex;flex-direction:column;gap:8px;}' +
      '.vd-cd-sh-lb{display:flex;align-items:center;gap:8px;font-size:9px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;color:#6A6A6A;}' +
      '.vd-cd-sh-lb b{color:#161616;font-size:10px;letter-spacing:.6px;}' +
      '.vd-cd-sh-sw{display:flex;gap:8px;flex-wrap:wrap;}' +
      '.vd-cd-sh-sw button{width:40px;height:40px;border-radius:50%;padding:0;border:none;background:none;cursor:pointer;position:relative;display:block;}' +
      '.vd-cd-sh-sw button span{display:block;width:100%;height:100%;border-radius:50%;overflow:hidden;border:1px solid #E0E0E0;background:#F4F4F4;box-sizing:border-box;transition:transform .15s;}' +
      '.vd-cd-sh-sw button img{width:100%;height:100%;object-fit:cover;transform:scale(1.5);display:block;}' +
      '.vd-cd-sh-sw button.is-cur{box-shadow:inset 0 0 0 2px #161616;}' +
      '.vd-cd-sh-sw button.is-cur span{transform:scale(.75);border-color:transparent;}' +
      '.vd-cd-sh-sw button:focus-visible{outline:2px solid #161616;outline-offset:2px;}' +
      '.vd-cd-sh-chips{display:flex;gap:8px;flex-wrap:wrap;}' +
      '.vd-cd-sh-ch{flex:0 0 auto;min-width:44px;height:36px;padding:0 10px;border:1px solid #161616;background:#fff;font:700 11px/1 var(--vd-font-body),Helvetica,Arial,sans-serif;letter-spacing:.04em;text-transform:uppercase;color:#161616;cursor:pointer;border-radius:0;}' +
      '.vd-cd-sh-ch[aria-pressed="true"]{background:#161616;color:#fff;}' +
      '.vd-cd-sh-ch:disabled{border-color:#E0E0E0;color:#B5B5B5;background:#fff;cursor:default;text-decoration:line-through;}' +
      '.vd-cd-sh-ch:focus-visible{outline:2px solid #161616;outline-offset:2px;}' +
      '.vd-cd-sh-add{display:flex;align-items:center;justify-content:center;gap:6px;width:100%;height:46px;border:none;background:#161616;color:#fff;font:700 12px/1 var(--vd-font-body),Helvetica,Arial,sans-serif;letter-spacing:1px;text-transform:uppercase;cursor:pointer;border-radius:0;}' +
      '.vd-cd-sh-add:disabled{background:#F4F4F4;color:#8A8A8A;cursor:default;}' +
      '.vd-cd-sh-note{font-size:10px;color:#8C8A85;padding:8px 0;}' +
      /* the line that was just added via the rail: 1.2s highlight after re-render */
      '.vd-cd-row.is-new{animation:vdCdNew 1.2s ease-out both;}' +
      '@keyframes vdCdNew{0%,55%{background:#F4F4F4}100%{background:transparent}}' +
      '.vd-cd-free{color:#D9211C;}' +
      '.vd-cd-giftlock{font-size:10px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#D9211C;}' +
      '.vd-ship__note{margin:8px 0 0;font-size:10px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:#D9211C;}' +
      '.vd-cd-busy{opacity:.55;pointer-events:none;}' +
      /* P0-3: whole-drawer lock while a cart write is in flight */
      '.vd-cd-writing .vd-cd-qt button,.vd-cd-writing .vd-cd-rm,.vd-cd-writing .vd-cd-ad{pointer-events:none;opacity:.5;}' +
      /* P0-2: the error surface that [data-cart-live] never had */
      '.vd-cd-err{margin:0 13px 8px;padding:8px 10px;background:#FDEDED;color:#8A1F11;' +
        'font:600 11px/1.35 var(--vd-font-body),Helvetica,Arial,sans-serif;text-transform:uppercase;letter-spacing:.03em;}' +
      'cart-drawer-items > *:not(.vd-cd){display:none!important;}';
    var st = document.createElement('style'); st.id = 'vd-cd-css'; st.textContent = css;
    (document.head || document.documentElement).appendChild(st);
  }

  // A/B config rendered as data attributes on <cart-drawer> by sections/cart-drawer.liquid.
  // In the control arm the gift/rail config is deliberately zeroed out so every downstream
  // branch collapses to V7 behaviour without needing its own if (isGift()) check.
  function drawerCfg() {
    var d = drawerEl();
    var g = function (a, f) { var v = d && d.getAttribute(a); return v == null || v === '' ? f : +v; };
    return {
      shipCents: g('data-ship', 99) * 100,
      giftCents: g('data-gift', 120) * 100,
      pct: g('data-pct', 0),
      giftPid: isGift() ? g('data-gift-product', 0) : 0,
      rail: isGift() ? (d && d.getAttribute('data-rail')) || '' : ''
    };
  }
  function wimg(u, w) { return u ? u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + w : ''; }
  function esc(s) { return (s || '').replace(/"/g, '&quot;'); }

  /* ==================================================================
     3. COLOURWAY GROUP INDEX  (Grouptify live + local snapshot fallback)
     ================================================================== */
  function groupIndex() {
    var groups = (window.mpg && window.mpg.groups) || [], idx = {};
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i]; if (g.is_active === false || g.show_on_card === false) continue;
      var ps = g.products || [];
      for (var j = 0; j < ps.length; j++) if (ps[j].handle && !idx[ps[j].handle]) idx[ps[j].handle] = g;
    }
    return idx;
  }
  var snapIdxCache = null, snapIdxN = -1;
  function domGroupIndex() {
    var fb = window.__VD_COLOR_GROUPS || [];
    if (snapIdxCache && snapIdxN === fb.length) return snapIdxCache;
    var idx = {};
    for (var i = 0; i < fb.length; i++) {
      var g = { products: fb[i].map(function (h) { return { handle: h }; }) };
      for (var j = 0; j < fb[i].length; j++) { if (!idx[fb[i][j]]) idx[fb[i][j]] = g; }
    }
    snapIdxCache = idx; snapIdxN = fb.length; return idx;
  }
  function siblings(g) {
    if (!g) return [];
    var seen = {}, out = [];
    (g.products || []).forEach(function (p) {
      if (p.handle && p.status !== 'ARCHIVED' && !seen[p.handle]) { seen[p.handle] = 1; out.push(p); }
    });
    return out;
  }
  function handleOf(url) { return (url || '').indexOf('/products/') > -1 ? url.split('/products/')[1].split(/[?#\/]/)[0] : ''; }
  function getProduct(handle) {
    if (pcache[handle]) return Promise.resolve(pcache[handle]);
    return fetch(R + 'products/' + handle + '.js').then(function (r) {
      if (!r.ok) throw new Error('product ' + r.status);
      return r.json();
    }).then(function (p) { pcache[handle] = p; return p; });
  }

  /* ==================================================================
     4. CART API  — P0-2 / P0-3 / P0-4
     ================================================================== */

  // P0-2: the old version was a bare fetch that never looked at response.ok, so a
  // 422 (over stock / over purchase limit) was treated as success. refresh() then
  // silently snapped the quantity back with zero feedback — that is the code-level
  // root cause of "the minus button does nothing" in the problem list.
  function api(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) {
          var err = new Error(j.description || j.message || ('Cart error ' + r.status));
          err.status = r.status; err.body = j;
          throw err;
        }
        return j;
      });
    });
  }

  function getCart() {
    return fetch(R + 'cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('cart fetch ' + r.status); return r.json(); });
  }

  // P0-4: was add.js -> change.js(0). If the second call failed the shopper was left
  // holding BOTH the old and the new variant, and the inflated intermediate subtotal
  // could false-trigger the gift. /cart/update.js does it in one atomic request.
  // NOTE: updates[variantId] SETS an absolute quantity, it does not add — so any
  // quantity of that variant already sitting on another line has to be counted first.
  function swap(oldKey, newVariantId, qty) {
    return getCart().then(function (cart) {
      var existing = 0, oldLine = null;
      cart.items.forEach(function (i) {
        if (i.key === oldKey) oldLine = i;
        if (i.variant_id === +newVariantId && i.key !== oldKey) existing += i.quantity;
      });
      // fuse (bug 2): a line "swapped" to its own variant is a no-op. Sending it would emit
      // {oldKey:0, variantId:qty} — two keys addressing ONE line — which /cart/update.js
      // resolves as a delete. Guards every caller, present and future, not just commitColour.
      if (oldLine && oldLine.variant_id === +newVariantId) return;
      var u = {};
      u[oldKey] = 0;
      u[String(newVariantId)] = existing + qty;
      emit('swap', { from_key: oldKey, to_variant: +newVariantId, qty: qty });
      return enqueue(function () { return api(R + 'cart/update.js', { updates: u }); });
    })
    .then(refresh)
    .catch(function (e) { showCartError(cartErr(e)); return refresh(); });
  }

  // P0-3: queued, and failures are now visible instead of silently snapping back.
  function setQty(key, q) {
    return enqueue(function () { return api(R + 'cart/change.js', { id: key, quantity: q }); })
      .then(refresh)
      .catch(function (e) { showCartError(cartErr(e)); return refresh(); });
  }

  /* ==================================================================
     5. DROPDOWNS
     ================================================================== */
  function closeDD(refocus) {
    // Every close path (outside-click, Esc, colour commit, tap-current) must detach the
    // outside-click listener. Before 2026-08-12 only the outside-click path detached it, so
    // each Esc/commit close leaked an armed listener that ate the shopper's NEXT click.
    document.removeEventListener('click', onDoc, true);
    var dd = document.querySelector('.vd-cd-dd'); if (!dd) return;
    var a = dd.__anchor; dd.remove();
    if (a && a.setAttribute) a.setAttribute('aria-expanded', 'false');
    if (refocus && a && document.contains(a)) { try { a.focus(); } catch (_) {} }
  }
  // a11y is fundamental, not part of the tested hypothesis: BOTH arms get
  // aria-expanded + focus management. Only the visual treatment differs.
  function openDD(anchor, dd) {
    anchor.parentNode.appendChild(dd);
    dd.__anchor = anchor;
    if (anchor.setAttribute) anchor.setAttribute('aria-expanded', 'true');
    var f = dd.querySelector('button:not([disabled]):not(.vd-cd-pop-x):not(.vd-cd-dd-x)'); if (f) try { f.focus(); } catch (_) {}
    setTimeout(function () { document.addEventListener('click', onDoc, true); }, 0);
  }
  // Escape closes the dropdown WITHOUT closing the drawer (capture phase beats the
  // drawer's document-level Escape listener in sections/cart-drawer.liquid)
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.querySelector('.vd-cd-dd')) { e.stopPropagation(); closeDD(true); }
  }, true);
  var pendingFocus = null;
  function onDoc(e) {
    if (!e.target.closest('.vd-cd-dd')) {
      // Hailin 2026-08-12 (final): while a picker is open, a click outside it means exactly ONE
      // thing — hide the picker. No exemptions: colour/size openers, qty steppers and Remove all
      // count as "outside" (the old .vd-cd-op carve-out let qty fire while the picker stayed
      // open, which read as the picker and the cart being entangled). Capture phase + swallow:
      // the dismissing click can never ALSO adjust a line, open another picker, or follow a
      // link underneath.
      e.preventDefault(); e.stopPropagation();
      closeDD();
    }
  }

  function colorName(s) {
    var t = s.title || (pcache[s.handle] && pcache[s.handle].title) || (s.handle || '').split('-').join(' ');
    var parts = t.split(' - ');
    return (parts.length > 1 ? parts[parts.length - 1] : t).toUpperCase();
  }

  // Shared commit path for both dropdown styles.
  function commitColour(anchor, s, item) {
    // manager review 2026-08-12 (bug 2): tapping the colour that's ALREADY in the cart is a
    // confirmation, not a swap. Without this guard it fell through to swap(), whose
    // {lineKey:0, sameVariantId:qty} self-referential update Shopify resolves by DELETING the line.
    if (s.handle === item.handle) { closeDD(true); return; }
    closeDD();
    var sizeVal = (item.sizeName || '');
    var row = anchor.closest('.vd-cd-row');
    row.classList.add('vd-cd-busy');
    getProduct(s.handle).then(function (p) {
      var v = (p.variants || []).filter(function (x) { return x.available !== false; });
      var pick = sizeVal ? (v.filter(function (x) {
        return (x.options || []).map(String).join(' ').toLowerCase().indexOf(sizeVal.toLowerCase()) > -1;
      })[0] || v[0]) : v[0];
      pick = pick || (p.variants || [])[0];
      // optimistic cosmetic bridge — swap()'s refresh() reconciles against the real cart
      var im = p.featured_image || (p.images && p.images[0]) || '';
      var rImg = row.querySelector('.vd-cd-row__img img'); if (rImg && im) rImg.src = wimg(im, 200);
      var rTi = row.querySelector('.vd-cd-ti'); if (rTi && p.title) rTi.textContent = dTitle(p.title);
      row.querySelectorAll('[data-color] .vd-cd-sw').forEach(function (el) {
        el.classList.toggle('is-cur', el.getAttribute('data-h') === s.handle);
      });
      pendingFocus = { idx: item.idx, sel: '[data-color]' };
      if (pick) swap(item.key, pick.id, item.qty);
    }).catch(function (e) {
      row.classList.remove('vd-cd-busy');
      showCartError(cartErr(e));
    });
  }

  // --- control arm: V7's plain list dropdown -------------------------
  function colorDropdownList(anchor, sibs, curHandle, item) {
    closeDD();
    var dd = document.createElement('div'); dd.className = 'vd-cd-dd';
    sibs.forEach(function (s) {
      var b = document.createElement('button');
      b.type = 'button';
      var im = (s.images && s.images[0]) || swImg[s.handle] || '';
      b.innerHTML = '<span class="vd-cd-sw' + (/shoulder/i.test(s.title || s.handle || '') ? ' vd-cd-sw--btm' : '') +
        (s.handle === curHandle ? ' is-cur' : '') + '">' + (im ? '<img src="' + esc(wimg(im, 140)) + '" alt="">' : '') +
        '</span><span>' + (s.title || s.handle) + '</span>';
      b.onclick = function () { commitColour(anchor, s, item); };
      dd.appendChild(b);
    });
    openDD(anchor, dd);
  }

  // --- gift arm: enlarged-swatch popover (Figma 687:2) ---------------
  function colorDropdownPopover(anchor, sibs, curHandle, item) {
    closeDD();
    var dd = document.createElement('div'); dd.className = 'vd-cd-dd vd-cd-pop';
    var ordered = sibs.filter(function (s) { return s.handle === curHandle; })
      .concat(sibs.filter(function (s) { return s.handle !== curHandle; }));
    var head = document.createElement('div'); head.className = 'vd-cd-pop-h';
    head.innerHTML = '<span class="vd-cd-pop-l">Color</span><span class="vd-cd-pop-n"></span>';
    var nameEl = head.lastChild;
    var curName = ordered.length ? colorName(ordered[0]) : colorName({ handle: curHandle, title: item.title });
    nameEl.textContent = curName;
    dd.appendChild(head);
    var grid = document.createElement('div'); grid.className = 'vd-cd-pop-g';
    dd.appendChild(grid);
    ordered.forEach(function (s) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vd-cd-swl' + (s.handle === curHandle ? ' is-cur' : '');
      var im = (s.images && s.images[0]) || swImg[s.handle] || '';
      b.innerHTML = '<span class="vd-cd-swl-d">' + (im ? '<img src="' + esc(wimg(im, 140)) + '" alt="">' : '') + '</span>';
      b.setAttribute('aria-label', 'Colour ' + colorName(s) + (s.handle === curHandle ? ' — selected' : ''));
      b.onmouseenter = function () { nameEl.textContent = colorName(s); };
      b.onmouseleave = function () { nameEl.textContent = curName; };
      getProduct(s.handle).then(function (p) {
        if (p && p.available === false) {
          b.classList.add('is-so');
          b.setAttribute('aria-label', 'Colour ' + colorName(s) + ' — sold out');
        }
      }).catch(function () {});
      b.onclick = function () { commitColour(anchor, s, item); };
      grid.appendChild(b);
    });
    openDD(anchor, dd);
  }

  function colorDropdown(anchor, sibs, curHandle, item) {
    emit('color_open', { key: item.key, handle: item.handle, options: sibs.length });
    return isGift() ? colorDropdownPopover(anchor, sibs, curHandle, item)
                    : colorDropdownList(anchor, sibs, curHandle, item);
  }

  function sizeDropdown(anchor, product, item) {
    closeDD();
    emit('size_open', { key: item.key, handle: item.handle });
    var dd = document.createElement('div'); dd.className = 'vd-cd-dd';
    (product.variants || []).forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button';
      var label = (v.options || []).join(' / ') || v.title;
      b.textContent = label + (v.available === false ? ' — sold out' : '');
      if (v.available === false) { b.disabled = true; b.style.opacity = '.4'; }
      b.onclick = function () {
        closeDD();
        if (v.id === item.variantId) return;
        var row = anchor.closest('.vd-cd-row');
        row.classList.add('vd-cd-busy');
        var szb = row.querySelector('.vd-cd-sz');
        if (szb) {
          var nsz = (v.options || []).join(' ') || v.title;
          szb.innerHTML = 'Size&nbsp; ' + nsz + ' ▾';
          szb.setAttribute('aria-label', 'Change size — ' + (item.title || '') + ', current size ' + nsz);
        }
        pendingFocus = { idx: item.idx, sel: '[data-size]' };
        swap(item.key, v.id, item.qty);
      };
      dd.appendChild(b);
    });
    openDD(anchor, dd);
  }

  /* ==================================================================
     6. ROW RENDERING
     ================================================================== */
  function rowHTML(item, idx) {
    var sibs = item.sibs, sizeOpt = item.sizeOpt, cfg = drawerCfg();
    // Gift arm only: the configured mystery gift renders as a locked line —
    // no stepper, no Remove, no colour/size ops. In the control arm cfg.giftPid
    // is 0, so this never fires.
    if (item.pid && item.pid === cfg.giftPid) {
      return '<div class="vd-cd-row" data-key="' + esc(item.key) + '" data-idx="' + idx + '">' +
        '<a class="vd-cd-row__img" href="' + esc(item.url) + '" aria-label="' + esc(item.title) + '">' +
        (item.image ? '<img src="' + esc(wimg(item.image, 200)) + '" alt="">' : '') + '</a>' +
        '<div class="vd-cd-bd">' +
        '<div class="vd-cd-tp"><span class="vd-cd-ti">' + esc(dTitle(item.title)) + '</span><span class="vd-cd-pr">' +
        (item.linePrice === 0 && item.origLine > 0
          ? '<s>' + money(item.origLine) + '</s><span class="vd-cd-free">FREE</span>'
          : money(item.linePrice)) +
        '</span></div>' +
        // P0-1 follow-through: this string used to concatenate a literal '$'
        '<div class="vd-cd-op"><span class="vd-cd-giftlock">Free gift · yours at ' + money(cfg.giftCents) + '+</span></div>' +
        '</div></div>';
    }
    var colorHTML = '';
    if (sibs.length > 1) {
      // current colour ALWAYS first in the visible strip — group order used to bury it
      // past the +N cutoff, so the ringed "selected" dot never showed.
      var sibsOrdered = sibs.filter(function (s) { return s.handle === item.handle; })
        .concat(sibs.filter(function (s) { return s.handle !== item.handle; }));
      var shown = sibsOrdered.slice(0, MAX_SW).map(function (s) {
        var im = (s.images && s.images[0]) || swImg[s.handle] || '';
        return '<span class="vd-cd-sw' + (/shoulder/i.test(s.title || s.handle || '') ? ' vd-cd-sw--btm' : '') +
          (s.handle === item.handle ? ' is-cur' : '') + '" data-h="' + esc(s.handle) + '">' +
          (im ? '<img src="' + esc(wimg(im, 140)) + '" alt="">' : '') + '</span>';
      }).join('');
      colorHTML = '<button type="button" class="vd-cd-op vd-cd-colorbtn" data-color="1" aria-haspopup="true" aria-expanded="false" aria-label="Change color — ' +
        esc(item.title) + '"><span class="vd-cd-lb">Color</span>' + shown +
        (sibs.length > MAX_SW ? '<span class="vd-cd-mo">+' + (sibs.length - MAX_SW) + '</span>' : '') +
        '<span class="vd-cd-cv"><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#A8A69E" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg></span></button>';
    }
    var sizeHTML = sizeOpt ? '<button type="button" class="vd-cd-sz" data-size="1" aria-haspopup="true" aria-expanded="false" aria-label="Change size — ' +
      esc(item.title) + ', current size ' + esc(item.sizeName) + '">Size&nbsp; ' + item.sizeName + ' ▾</button>' : '';
    return '<div class="vd-cd-row" data-key="' + esc(item.key) + '" data-idx="' + idx + '">' +
      '<a class="vd-cd-row__img' + (/shoulder/i.test(item.title || '') ? ' vd-cd-row__img--btm' : '') + '" href="' + esc(item.url) + '" aria-label="' + esc(item.title) + '">' +
      (item.image ? '<img src="' + esc(wimg(item.image, 200)) + '" alt="">' : '') + '</a>' +
      '<div class="vd-cd-bd">' +
      '<div class="vd-cd-tp"><span class="vd-cd-ti">' + esc(dTitle(item.title)) + '</span><span class="vd-cd-pr">' +
      (item.linePrice === 0 && item.origLine > 0
        ? '<s>' + money(item.origLine) + '</s><span class="vd-cd-free">FREE</span>'
        : ((item.compareAt && item.compareAt > item.unit) ? '<s>' + money(item.compareAt * item.qty) + '</s>' : '') + money(item.linePrice)) +
      '</span></div>' +
      colorHTML +
      '<div class="vd-cd-op">' + sizeHTML +
      '<div class="vd-cd-qt">' +
      '<button type="button" data-q="dec" aria-label="Decrease quantity — ' + esc(item.title) + '"' + (item.qty <= 1 ? ' disabled aria-disabled="true"' : '') + '>−</button>' +
      '<span>' + item.qty + '</span>' +
      '<button type="button" data-q="inc" aria-label="Increase quantity — ' + esc(item.title) + '">+</button></div>' +
      '<button type="button" class="vd-cd-rm" aria-label="Remove ' + esc(item.title) + ' from bag">Remove</button></div>' +
      '</div></div>';
  }

  /* ==================================================================
     7. RECOMMENDATION RAIL
     control = Shopify recommendations, diversified by product type (V7)
     gift    = sale collection ranked by gap-to-threshold + wishlist weave
     ================================================================== */
  function diversify(pool, cartIds) {
    var seen = {}, byType = {}, order = [];
    pool.forEach(function (p) {
      if (cartIds[p.id] || seen[p.id]) return; seen[p.id] = 1;
      var t = p.type || '_'; if (!byType[t]) { byType[t] = []; order.push(t); } byType[t].push(p);
    });
    var out = [], ti = 0, guard = 0;
    while (out.length < MAX_REC && guard++ < 200) {
      var t = order[ti % order.length]; if (byType[t] && byType[t].length) out.push(byType[t].shift());
      ti++; if (order.every(function (x) { return !byType[x].length; })) break;
    }
    return out;
  }

  // ---- rail cards (2026-10-02, quick-add SHEET — Hailin's redesign) ----
  // One renderer for both arms (gift arm passes normalised products, control arm passes
  // raw recommendation products; both carry `variants`). Card = image + title + price +
  // one "+ Add". No swatch dots on the card: they were decorative <span>s that read as
  // tappable. The WHOLE card is the hit area — image, title and ADD all open the quick-add
  // sheet — except a one-variant product with no colour siblings, whose ADD adds directly
  // (its image/title still open the sheet as a mini product view).
  // Standardised vector icons (Hailin: icons are never text glyphs). Same stroke language as
  // the drawer's progress-band check (sections/cart-drawer.liquid .vd-ship__chk).
  var ICON_PLUS = '<svg class="vd-cd-ic" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="M5 1.5v7M1.5 5h7" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>';
  var ICON_CHECK = '<svg class="vd-cd-ic" width="10" height="10" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2.4 6.4 5 9l4.6-5.6" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ADD_LABEL = ICON_PLUS + '<span>Add</span>';
  function railSibs(p, idx) { var g = idx && idx[p.handle]; return g ? siblings(g) : []; }
  function railHasOptions(p, idx) {
    var vs = p.variants || [];
    return vs.length > 1 || railSibs(p, idx).length > 1;
  }
  function railCard(p, idx) {
    var im = p.featured_image || (p.images && p.images[0]) || '';
    var priceC = typeof p.price === 'string' ? Math.round(parseFloat(p.price) * 100) : p.price;
    var cmpC = typeof p.compare_at_price === 'string' ? Math.round(parseFloat(p.compare_at_price) * 100) : (p.compare_at_price || 0);
    var onSale = cmpC && cmpC > priceC;
    var vs = p.variants || [];
    var avail = vs.filter(function (v) { return v.available !== false; });
    var hasOptions = railHasOptions(p, idx);
    var vid = p.variant_id || (avail[0] && avail[0].id) || (vs[0] && vs[0].id) || '';
    var url = R + 'products/' + esc(p.handle);
    var title = dTitle(p.title || '');
    return '<div class="vd-cd-cc" data-rec="' + esc(p.handle) + '">' +
      '<a class="vd-cd-ci" href="' + url + '" data-sheet="' + esc(p.handle) + '" aria-label="' + esc(title) + '">' + (im ? '<img src="' + esc(wimg(im, 260)) + '" alt="" loading="lazy">' : '') + '</a>' +
      '<div class="vd-cd-cb">' +
      '<a class="vd-cd-ct" href="' + url + '" data-sheet="' + esc(p.handle) + '" tabindex="-1" aria-hidden="true">' + esc(title) + '</a>' +
      '<div class="vd-cd-cp">' + (onSale ? '<s>' + money(cmpC) + '</s><span class="vd-cd-now">' + money(priceC) + '</span>' : money(priceC)) + '</div>' +
      (hasOptions
        ? '<button type="button" class="vd-cd-ad" data-sheet="' + esc(p.handle) + '" aria-haspopup="dialog" aria-label="Choose options — ' + esc(title) + '">' + ADD_LABEL + '</button>'
        : '<button type="button" class="vd-cd-ad" data-add="' + vid + '" aria-label="Add ' + esc(title) + ' to bag">' + ADD_LABEL + '</button>') +
      '</div></div>';
  }
  function recHTMLControl(prods, idx) {
    if (!prods.length) return '';
    return prods.map(function (p) { return railCard(p, idx); }).join('');
  }
  function recCardsHTML(prods) {
    if (!prods.length) return '';
    var idx = Object.assign(domGroupIndex(), groupIndex());
    return prods.map(function (p) { return railCard(p, idx); }).join('');
  }
  function recWrap(cards) {
    if (!cards) return '';
    return '<div class="vd-cd-rec"><h4>Make it Von Dutch · you may also like</h4><div class="vd-cd-rr">' + cards + '</div></div>';
  }

  // After an add, the new line is highlighted once the drawer re-renders (newest line
  // sits on top; Shopify returns the line's key from /cart/add.js).
  var newLineKey = '';
  function flagNewLine(line) { newLineKey = (line && line.key) || ''; }
  function highlightNewLine(host, wrap) {
    if (!newLineKey) return;
    var key = newLineKey; newLineKey = '';
    var row = wrap.querySelector('.vd-cd-row[data-key="' + key.replace(/"/g, '') + '"]');
    if (!row) return;
    row.classList.add('is-new');
    try { host.scrollTop = 0; } catch (_) {}
    setTimeout(function () { row.classList.remove('is-new'); }, 1400);
  }

  // Direct add (one-size, no siblings) + sheet openers.
  // P1-12: disabled state + failure recovery — a failed add used to leave "Adding…" forever.
  function bindRecAdds(mount) {
    mount.querySelectorAll('.vd-cd-ad[data-add]').forEach(function (b) {
      b.onclick = function () {
        if (b.disabled) return;
        var id = +b.getAttribute('data-add');
        if (!id) return;
        b.disabled = true;
        b.innerHTML = '<span>Adding…</span>';
        emit('rec_add', { variant_id: id, via: 'direct' });
        enqueue(function () { return api(R + 'cart/add.js', { id: id, quantity: 1 }); })
          .then(function (line) { b.innerHTML = ICON_CHECK + '<span>Added</span>'; b.classList.add('is-added'); flagNewLine(line); return refresh(); })
          .catch(function (e) { showCartError(cartErr(e)); b.disabled = false; b.innerHTML = ADD_LABEL; });
      };
    });
    mount.querySelectorAll('[data-sheet]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // let "open in new tab" through
        e.preventDefault();
        var card = el.closest('.vd-cd-cc');
        var opener = (card && card.querySelector('.vd-cd-ad')) || el; // focus returns to the card's ADD on close
        openSheet(el.getAttribute('data-sheet'), opener, el.tagName === 'BUTTON' ? 'add' : 'card');
      });
    });
  }

  // Every drawer refresh rebuilds the rail. The sheet lives outside the rail (on the drawer
  // panel), so an open sheet survives the rebuild untouched.
  function mountRail(mount, cards) {
    mount.innerHTML = recWrap(cards);
    bindRecAdds(mount);
  }

  /* ---- quick-add SHEET (Hailin, 2026-10-02) ----
     Slides up inside the drawer panel, over the footer. Dismissal = scrim tap or Esc; the
     dismissing click is swallowed and there is NO × — the same model Hailin set for the
     colour popover on 2026-08-12. No pre-selected size (same rule as Shop the Look); the CTA
     is gated SELECT SIZE → ADD TO BAG · $price. Colour = sibling products from the colour
     groups; switching colour re-renders sizes from that product and a chosen size carries
     over ONLY if it exists and is in stock there — otherwise it is cleared, never silently
     substituted (cart bug 3). */
  var sheetState = null; // { handle, opener, sel:{optName:value}, root, el, focused }
  function sheetRoot() {
    var d = drawerEl(); if (!d) return null;
    var panel = d.querySelector('.vd-drawer__panel') || d;
    var root = panel.querySelector('.vd-cd-sheet-root');
    if (!root) {
      root = document.createElement('div');
      root.className = 'vd-cd-sheet-root';
      root.hidden = true;
      root.innerHTML = '<div class="vd-cd-scrim" data-cd-scrim></div>' +
        '<section class="vd-cd-sheet" role="dialog" aria-modal="true" aria-label="Choose options" tabindex="-1"></section>';
      // appending to the panel is a childList mutation the drawer observer would treat as
      // "someone else changed the cart" → pause it for this one write
      if (mo) mo.disconnect();
      panel.appendChild(root);
      requestAnimationFrame(function () { requestAnimationFrame(observeOn); });
      root.querySelector('[data-cd-scrim]').addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation(); closeSheet(true);
      });
    }
    return root;
  }
  function closeSheet(refocus) {
    var st = sheetState; if (!st) return;
    sheetState = null;
    st.root.classList.remove('is-in');
    setTimeout(function () {
      if (sheetState) return;
      st.root.hidden = true;
      st.el.innerHTML = '';
    }, 300);
    if (refocus && st.opener && document.contains(st.opener)) { try { st.opener.focus({ preventScroll: true }); } catch (_) {} }
  }
  // Escape closes the sheet WITHOUT closing the drawer (capture beats the drawer's own
  // document-level Escape listener in sections/cart-drawer.liquid). A colour picker, if
  // one is somehow open, takes priority and is handled by its own listener above.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheetState && !document.querySelector('.vd-cd-dd')) { e.stopPropagation(); e.preventDefault(); closeSheet(true); }
  }, true);
  function openSheet(handle, opener, via) {
    var root = sheetRoot(); if (!root || !handle) return;
    if (sheetState) closeSheet(false);
    closeDD(false);
    var el = root.querySelector('.vd-cd-sheet');
    sheetState = { handle: handle, opener: opener, sel: {}, root: root, el: el, focused: false };
    var st = sheetState;
    el.innerHTML = '<div class="vd-cd-sh-note">Loading…</div>';
    root.hidden = false;
    void root.offsetWidth;              // flush styles so the slide-up transition runs (no rAF: frozen in background tabs)
    root.classList.add('is-in');
    emit('rec_sheet_open', { handle: handle, via: via || 'card' });
    getProduct(handle).then(function (p) { if (sheetState === st) renderSheet(p); })
      .catch(function (e) { showCartError(cartErr(e)); if (sheetState === st) closeSheet(true); });
  }
  function cfgOptions(p) {
    // /products/{handle}.js: options = [{name, position, values}]; skip the placeholder "Title".
    return (p.options || []).filter(function (o) { return o && o.name && !/^title$/i.test(o.name) && (o.values || []).length; });
  }
  function cfgVariantFor(p, sel, opts) {
    var vs = p.variants || [];
    for (var i = 0; i < vs.length; i++) {
      var v = vs[i], ok = true;
      for (var j = 0; j < opts.length; j++) {
        var val = sel[opts[j].name];
        if (val == null) { ok = false; break; }
        if (v['option' + opts[j].position] !== val) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }
  function cfgValueAvailable(p, opts, name, value, sel) {
    // available if some in-stock variant has this value, holding the OTHER chosen options fixed
    return (p.variants || []).some(function (v) {
      if (v.available === false) return false;
      for (var j = 0; j < opts.length; j++) {
        var o = opts[j], want = o.name === name ? value : sel[o.name];
        if (want != null && v['option' + o.position] !== want) return false;
      }
      return true;
    });
  }
  function renderSheet(p) {
    var st = sheetState; if (!st) return;
    var opts = cfgOptions(p);
    // single value → auto-select (ONE SIZE). A size carried over from a sibling survives
    // only if that value exists AND is in stock on this product; otherwise it is cleared.
    opts.forEach(function (o) {
      var cur = st.sel[o.name];
      if (o.values.length === 1) { st.sel[o.name] = o.values[0]; return; }
      if (cur != null && (o.values.indexOf(cur) < 0 || !cfgValueAvailable(p, opts, o.name, cur, st.sel))) delete st.sel[o.name];
    });
    var idx = Object.assign(domGroupIndex(), groupIndex());
    var sibs = siblings(idx[p.handle]);
    var v = cfgVariantFor(p, st.sel, opts);
    var first = (p.variants || []).filter(function (x) { return x.available !== false; })[0] || (p.variants || [])[0] || {};
    var priceC = v ? v.price : (first.price || 0);
    var cmpC = v ? (v.compare_at_price || 0) : (first.compare_at_price || 0);
    var im = (v && v.featured_image && v.featured_image.src) || p.featured_image || (p.images && p.images[0]) || '';
    var url = R + 'products/' + esc(p.handle);
    var title = dTitle(p.title || '');
    var html = '<div class="vd-cd-sh-hd">' +
      '<a class="vd-cd-sh-img" href="' + url + '" tabindex="-1" aria-hidden="true">' + (im ? '<img src="' + esc(wimg(im, 200)) + '" alt="">' : '') + '</a>' +
      '<div class="vd-cd-sh-meta"><span class="vd-cd-sh-ti">' + esc(title) + '</span>' +
      '<span class="vd-cd-sh-pr" data-sh-price>' + (cmpC > priceC ? '<s>' + money(cmpC) + '</s><span class="vd-cd-now">' + money(priceC) + '</span>' : money(priceC)) + '</span>' +
      '<a class="vd-cd-sh-view" href="' + url + '">View details</a></div></div>';
    if (sibs.length > 1) {
      html += '<div class="vd-cd-sh-row"><span class="vd-cd-sh-lb">Colour<b>' + esc(colourName(p, sibs)) + '</b></span><div class="vd-cd-sh-sw">' +
        sibs.map(function (sb) {
          var si = (sb.images && sb.images[0]) || swImg[sb.handle] || '';
          var cur = sb.handle === p.handle;
          return '<button type="button" data-sh-sib="' + esc(sb.handle) + '" class="' + (cur ? 'is-cur' : '') + '" aria-label="' + esc(sb.title || sb.handle) + '"' + (cur ? ' aria-current="true"' : '') + '><span>' + (si ? '<img src="' + esc(wimg(si, 120)) + '" alt="">' : '') + '</span></button>';
        }).join('') + '</div></div>';
    }
    opts.forEach(function (o) {
      if (o.values.length < 2) return;
      html += '<div class="vd-cd-sh-row"><span class="vd-cd-sh-lb">' + esc(o.name) + (st.sel[o.name] ? '<b>' + esc(st.sel[o.name]) + '</b>' : '') + '</span><div class="vd-cd-sh-chips">' +
        o.values.map(function (val) {
          var ok = cfgValueAvailable(p, opts, o.name, val, st.sel);
          return '<button type="button" class="vd-cd-sh-ch" data-sh-opt="' + esc(o.name) + '" data-sh-val="' + esc(val) + '" aria-pressed="' + (st.sel[o.name] === val ? 'true' : 'false') + '"' + (ok ? '' : ' disabled aria-disabled="true"') + '>' + esc(val) + '</button>';
        }).join('') + '</div></div>';
    });
    var ready = !!(v && v.available !== false);
    var missing = opts.filter(function (o) { return o.values.length > 1 && st.sel[o.name] == null; }).map(function (o) { return o.name; });
    html += '<button type="button" class="vd-cd-sh-add" data-sh-add' + (ready ? '' : ' disabled') + '>' +
      (missing.length ? 'Select ' + missing.join(' & ') : (v && v.available === false ? 'Sold out' : 'Add to bag · ' + money(priceC))) + '</button>';
    var el = st.el;
    el.innerHTML = html;
    el.setAttribute('aria-label', 'Choose options — ' + title);

    el.querySelectorAll('[data-sh-sib]').forEach(function (b) {
      b.onclick = function () {
        var h = b.getAttribute('data-sh-sib'); if (h === p.handle) return;
        st.handle = h;
        emit('rec_colour_switch', { from: p.handle, to: h });
        el.setAttribute('aria-busy', 'true');
        getProduct(h).then(function (np) { if (sheetState === st) { renderSheet(np); var nb = el.querySelector('[data-sh-sib="' + h + '"]'); if (nb) try { nb.focus({ preventScroll: true }); } catch (_) {} } })
          .catch(function (e) { showCartError(cartErr(e)); el.removeAttribute('aria-busy'); });
      };
    });
    // Siblings the colour-group feed lists without an image: fill from their own product JSON (cached).
    el.querySelectorAll('[data-sh-sib]').forEach(function (b) {
      if (b.querySelector('img')) return;
      var h = b.getAttribute('data-sh-sib');
      getProduct(h).then(function (sp) {
        var si = sp.featured_image || (sp.images && sp.images[0]) || '';
        if (!si) return;
        swImg[h] = si;
        var sp2 = b.querySelector('span'); if (sp2 && !sp2.querySelector('img')) sp2.innerHTML = '<img src="' + esc(wimg(si, 120)) + '" alt="">';
      }).catch(function () {});
    });
    el.querySelectorAll('[data-sh-opt]').forEach(function (b) {
      b.onclick = function () {
        if (b.disabled) return;
        var name = b.getAttribute('data-sh-opt'), val = b.getAttribute('data-sh-val');
        if (st.sel[name] === val) delete st.sel[name]; else st.sel[name] = val;
        emit('rec_size_select', { handle: p.handle, option: name, value: st.sel[name] || null });
        renderSheet(p);
        var nb = el.querySelector('[data-sh-opt="' + name + '"][data-sh-val="' + val + '"]'); if (nb) try { nb.focus({ preventScroll: true }); } catch (_) {}
      };
    });
    var addBtn = el.querySelector('[data-sh-add]');
    addBtn.onclick = function () {
      if (addBtn.disabled || !v) return;
      addBtn.disabled = true; addBtn.textContent = 'Adding…';
      emit('rec_add', { variant_id: v.id, via: 'sheet' });
      enqueue(function () { return api(R + 'cart/add.js', { id: v.id, quantity: 1 }); })
        .then(function (line) { addBtn.innerHTML = ICON_CHECK + '<span>Added</span>'; flagNewLine(line); closeSheet(false); return refresh(); })
        .catch(function (e) { showCartError(cartErr(e)); addBtn.disabled = false; addBtn.textContent = 'Add to bag · ' + money(priceC); });
    };
    el.removeAttribute('aria-busy');
    if (!st.focused) {
      st.focused = true;
      var f = el.querySelector('.vd-cd-sh-ch:not(:disabled)') || el.querySelector('[data-sh-sib]') || el.querySelector('[data-sh-add]');
      if (f) try { f.focus({ preventScroll: true }); } catch (_) {}
    }
  }
  // "Black" from "Women's Active Leggings - Black" style titles; falls back to the sibling's own title
  function colourName(p, sibs) {
    var t = p.title || '';
    var m = t.match(/\s[-–—]\s([^-–—]+)$/);
    if (m) return m[1].trim();
    var cur = sibs.filter(function (s) { return s.handle === p.handle; })[0];
    return cur && cur.title && cur.title !== t ? cur.title : '';
  }
  function fetchRecs(cart, cb) {
    var seeds = cart.items.slice(0, 3), pool = [], done = 0;
    if (!seeds.length) return cb([]);
    seeds.forEach(function (it) {
      fetch(R + 'recommendations/products.json?product_id=' + it.product_id + '&intent=related&limit=8')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (j) { pool = pool.concat(j.products || []); })
        .catch(function () {}).then(function () { if (++done === seeds.length) cb(pool); });
    });
  }

  function getWishlist() {
    try { var v = JSON.parse(localStorage.getItem('vd_wishlist') || '[]'); return Array.isArray(v) ? v.filter(Boolean) : []; }
    catch (e) { return []; }
  }
  // /collections/{h}/products.json → prices are DOLLAR STRINGS
  function normCollectionProduct(p) {
    var v = (p.variants || []).filter(function (x) { return x.available; })[0];
    if (!v) return null;
    var pr = Math.round(parseFloat(v.price) * 100) || 0;
    var cmp = Math.round(parseFloat(v.compare_at_price || 0) * 100) || 0;
    return { id: p.id, handle: p.handle, title: p.title,
      featured_image: (p.images && p.images[0] && p.images[0].src) || '',
      price: pr, compare_at_price: cmp > pr ? cmp : 0, variant_id: v.id };
  }
  // /products/{h}.js and /recommendations/products.json → prices are CENTS
  function normJsProduct(p) {
    if (!p || p.available === false) return null;
    var v = (p.variants || []).filter(function (x) { return x.available !== false; })[0];
    if (!v) return null;
    var pr = parseInt(v.price, 10) || parseInt(p.price, 10) || 0;
    var cmp = parseInt(v.compare_at_price, 10) || 0;
    return { id: p.id, handle: p.handle, title: p.title,
      featured_image: p.featured_image || (p.images && p.images[0]) || '',
      price: pr, compare_at_price: cmp > pr ? cmp : 0, variant_id: v.id };
  }

  // gift arm rail. NOTE (P1-10, still open): products.json?limit=50 returns every
  // variant and image for 50 products — hundreds of KB, fetched at the exact moment
  // a mobile shopper opens the drawer, i.e. on top of the metric being tested.
  // The right fix is a purpose-built section (?sections=vd-cd-rail) emitting only
  // handle/title/image/price. Left as-is here so this patch stays behaviour-preserving.
  function fetchRail(cart, cb) {
    var cfg = drawerCfg();
    var railHandle = cfg.rail;
    var cartIds = {}; cart.items.forEach(function (i) { cartIds[i.product_id] = 1; });
    var giftPid = cfg.giftPid; if (giftPid) cartIds[giftPid] = 1;
    if (!railHandle) {
      return fetchRecs(cart, function (pool) {
        cb(pool.map(normJsProduct).filter(function (p) { return p && !cartIds[p.id]; }).slice(0, MAX_REC));
      });
    }
    var giftLine = 0;
    cart.items.forEach(function (i) { if (giftPid && i.product_id === giftPid) giftLine += i.final_line_price; });
    var gap = Math.max(0, cfg.giftCents - (cart.total_price - giftLine));
    function gapScore(p) { var d = p.price - gap; return d >= 0 ? d : -d * 1.5; }
    var salePromise = fetch(R + 'collections/' + encodeURIComponent(railHandle) + '/products.json?limit=50')
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (j) {
        return (j.products || []).map(normCollectionProduct).filter(Boolean)
          .sort(function (a, b) { return gapScore(a) - gapScore(b); });
      }).catch(function () { return []; });
    var wishPromise = Promise.all(getWishlist().slice(0, 10).map(function (h) {
      return getProduct(h).then(normJsProduct).catch(function () { return null; });
    })).then(function (list) { return list.filter(Boolean); }).catch(function () { return []; });
    Promise.all([salePromise, wishPromise]).then(function (res) {
      function usable(arr) { var s = {}; return arr.filter(function (p) { if (!p || cartIds[p.id] || s[p.id]) return false; s[p.id] = 1; return true; }); }
      var wish = usable(res[1]);
      var wishIds = {}; wish.forEach(function (p) { wishIds[p.id] = 1; });
      var sale = usable(res[0]).filter(function (p) { return !wishIds[p.id]; });
      var out = [], si = 0, wi = 0, slot = 0;
      while (out.length < MAX_REC && (si < sale.length || wi < wish.length)) {
        var take = (slot % 3 === 2) ? (wish[wi++] || sale[si++]) : (sale[si++] || wish[wi++]);
        slot++;
        if (take) out.push(take);
      }
      cb(out);
    });
  }

  /* ==================================================================
     8. GIFT ENFORCEMENT  — P0-5
     ================================================================== */
  // The old rule was "theme computes the threshold, theme deletes the line".
  // After moving the promotion to a Shopify Buy X Get Y automatic discount the
  // authority flips: Shopify decides whether the gift is free, and the theme only
  // reacts to what it sees. Criterion is now final_line_price > 0 — i.e. "the
  // shopper is about to be CHARGED for a free gift" — and every gift line is
  // removed, not just gl[0].
  //
  // HARD PREREQUISITE: this is only correct if the automatic discount applies in
  // the AJAX cart, not only at checkout. Verify with a test discount:
  //   fetch('/cart.js').then(r=>r.json()).then(c=>console.log(c.discount_applications))
  // If the discount only lands at checkout, this guard would delete the gift the
  // instant it is added (add -> delete -> add flicker). In that case set
  // data-gift-enforce="subtotal" on <cart-drawer> to fall back to the old
  // subtotal rule and change the copy to "Free at checkout".
  function enforceGift(cart) {
    var cfg = drawerCfg();
    var giftPid = cfg.giftPid;
    if (!giftPid || giftBusy) return Promise.resolve(false);
    var gl = cart.items.filter(function (i) { return i.product_id === giftPid; });
    if (!gl.length) return Promise.resolve(false);

    var mode = (drawerEl() && drawerEl().getAttribute('data-gift-enforce')) || 'subtotal';
    var doomed;
    if (mode === 'subtotal') {
      // Threshold not met -> the shopper is not entitled to a gift at all: remove every line.
      var giftTotal = gl.reduce(function (a, i) { return a + i.final_line_price; }, 0);
      doomed = ((cart.total_price - giftTotal) < cfg.giftCents) ? gl : [];
    } else {
      // BXGY mode: Shopify decides. Remove ONLY the lines it did not zero out — with
      // "max uses per order = 1" a second gift line stays at full price, and killing the
      // free one alongside it would take away a gift the shopper had legitimately earned.
      doomed = gl.filter(function (i) { return i.final_line_price > 0; });
    }
    if (!doomed.length) return Promise.resolve(false);

    giftBusy = true;
    var u = {};
    doomed.forEach(function (i) { u[i.key] = 0; });
    var away = Math.max(0, cfg.giftCents - cart.total_price);
    emit('gift_lost', { away_cents: away, mode: mode, lines_removed: doomed.length });
    return enqueue(function () { return api(R + 'cart/update.js', { updates: u }); })
      .then(function () { giftBusy = false; giftNoticePending = cart.total_price; return true; })
      .catch(function (e) { giftBusy = false; showCartError(cartErr(e)); return false; });
  }

  /* ==================================================================
     9. RENDER LOOP
     ================================================================== */
  var mo = null, cdEl = null;
  function observeOn() { if (mo && cdEl) mo.observe(cdEl, { childList: true, subtree: true }); }

  function refresh() {
    if (mo) mo.disconnect();   // pause: our own DOM writes must not re-trigger us
    return getCart().then(function (cart) {
      var idx = Object.assign(domGroupIndex(), groupIndex());
      var cartIds = {}; cart.items.forEach(function (i) { cartIds[i.product_id] = 1; });
      var cfg = drawerCfg(), giftPid = cfg.giftPid;
      if (giftPid) cartIds[giftPid] = 1;   // never recommend the gift

      enforceGift(cart).then(function (removed) { if (removed) schedule(); });

      // telemetry: unlock / re-lock transitions (emitted once per crossing)
      if (giftPid || isGift()) {
        var giftTotal = cart.items.reduce(function (a, i) { return a + (i.product_id === giftPid ? i.final_line_price : 0); }, 0);
        var unlocked = (cart.total_price - giftTotal) >= cfg.giftCents;
        if (lastGiftUnlocked !== null && unlocked !== lastGiftUnlocked && unlocked) {
          emit('gift_unlock', { total_price: cart.total_price });
        }
        lastGiftUnlocked = unlocked;
      }

      var liveMsg = cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ' in bag, subtotal ' + money(cart.total_price);
      if (lastLive && liveMsg !== lastLive && giftNoticePending == null && !giftBusy) announce(liveMsg);
      lastLive = liveMsg;

      var items = cart.items.map(function (it, i) {
        var handle = handleOf(it.url), g = idx[handle], sibs = siblings(g);
        var sizeOpt = (it.options_with_values || []).filter(function (o) { return /size/i.test(o.name); })[0];
        return { key: it.key, idx: i, handle: handle, url: it.url, image: it.image, title: it.product_title,
          linePrice: it.final_line_price, unit: it.final_price, compareAt: saleCache[it.variant_id],
          origLine: it.original_line_price, pid: it.product_id,
          qty: it.quantity, variantId: it.variant_id, sibs: sibs,
          sizeOpt: sizeOpt, sizeName: sizeOpt ? sizeOpt.value : '' };
      });

      var host = document.querySelector('cart-drawer-items');
      if (!host) return;
      var wrap = host.querySelector('.vd-cd') || document.createElement('div');
      wrap.className = 'vd-cd';
      wrap.innerHTML = items.map(rowHTML).join('') + '<div class="vd-cd-rec-mount"></div>';
      if (!wrap.parentNode) host.appendChild(wrap);
      // P1-8: Liquid's fallback list is hidden rather than removed, so an 11-line cart
      // carries two copies of the DOM. Removing it outright would break Dawn's own
      // re-render contract, so it stays hidden — flagged, not silently accepted.
      host.querySelectorAll(':scope > *:not(.vd-cd)').forEach(function (el) { el.style.display = 'none'; });
      wire(wrap, items);
      highlightNewLine(host, wrap);

      if (pendingFocus != null) {
        var pf = wrap.querySelector('.vd-cd-row[data-idx="' + pendingFocus.idx + '"] ' + pendingFocus.sel);
        pendingFocus = null;
        if (pf) try { pf.focus(); } catch (_) {}
      }

      enrichSale(items);
      enrichSwatchImages(items);

      var mount = wrap.querySelector('.vd-cd-rec-mount');
      if (mount) {
        // cache key includes the total: gap-based ranking must re-sort when the amount moves,
        // and a colour/size swap keeps the line count identical.
        var sig = variant() + ':' + cart.items.length + ':' + cart.total_price;
        if (sig === recSig && recCache) { mount.innerHTML = recWrap(recCache); bindRecAdds(mount); }
        else if (isGift()) {
          fetchRail(cart, function (prods) {
            recSig = sig; recCache = recCardsHTML(prods);
            var m2 = wrap.querySelector('.vd-cd-rec-mount'); if (!m2) return;
            m2.innerHTML = recWrap(recCache); bindRecAdds(m2);
          });
        } else {
          fetchRecs(cart, function (pool) {
            recSig = sig; recCache = recHTMLControl(diversify(pool, cartIds), idx);
            var m2 = wrap.querySelector('.vd-cd-rec-mount'); if (!m2) return;
            m2.innerHTML = recWrap(recCache); bindRecAdds(m2);
          });
        }
      }
      return refreshChrome();
    }).then(function () {
      requestAnimationFrame(function () { requestAnimationFrame(observeOn); });
    }).catch(function (e) {
      observeOn();
      emit('error', { where: 'refresh', message: (e && e.message) || '' });
    });
  }

  function wire(wrap, items) {
    wrap.querySelectorAll('.vd-cd-row').forEach(function (row) {
      var item = items[+row.getAttribute('data-idx')];
      row.querySelectorAll('.vd-cd-qt button').forEach(function (b) {
        b.onclick = function () {
          if (b.disabled) return;
          var dir = b.getAttribute('data-q') === 'inc' ? 1 : -1;
          var nq = item.qty + dir;
          if (nq < 1) return;   // qty=1 → "−" is disabled; deletion goes through Remove
          row.classList.add('vd-cd-busy');
          emit('qty', { key: item.key, handle: item.handle, from: item.qty, to: nq, dir: dir > 0 ? 'inc' : 'dec' });
          setQty(item.key, nq);
        };
      });
      var rm = row.querySelector('.vd-cd-rm');
      if (rm) rm.onclick = function () {
        row.classList.add('vd-cd-busy');
        emit('remove', { key: item.key, handle: item.handle, qty: item.qty });
        setQty(item.key, 0);
      };
      var colorOp = row.querySelector('[data-color]');
      if (colorOp) colorOp.onclick = function (e) { e.stopPropagation(); colorDropdown(colorOp, item.sibs, item.handle, item); };
      var sizeBtn = row.querySelector('[data-size]');
      if (sizeBtn) sizeBtn.onclick = function (e) {
        e.stopPropagation();
        getProduct(item.handle).then(function (p) { sizeDropdown(sizeBtn, p, item); })
          .catch(function (err) { showCartError(cartErr(err)); });
      };
    });
  }

  /* ==================================================================
     10. ENRICHMENT  (compare-at prices, sibling swatch images)
     ================================================================== */
  function enrichSale(items) {
    var pend = items.filter(function (it) { return saleCache[it.variantId] === undefined; });
    if (!pend.length) return;
    var handles = {}; pend.forEach(function (it) { handles[it.handle] = 1; });
    var hs = Object.keys(handles), done = 0;
    hs.forEach(function (h) {
      getProduct(h).then(function (p) {
        (p.variants || []).forEach(function (v) {
          var cmp = parseInt(v.compare_at_price, 10) || 0, pr = parseInt(v.price, 10) || 0;
          saleCache[v.id] = (cmp > pr) ? cmp : 0;
        });
      }).catch(function () {}).then(function () {
        pend.forEach(function (it) { if (it.handle === h && saleCache[it.variantId] === undefined) saleCache[it.variantId] = 0; });
        if (++done === hs.length && items.some(function (it) { return saleCache[it.variantId] > 0; })) schedule();
      });
    });
  }

  var swImg = {};
  function enrichSwatchImages(items) {
    var need = {};
    items.forEach(function (it) {
      (it.sibs || []).forEach(function (s) {
        if (s.handle && !(s.images && s.images[0]) && !swImg[s.handle]) need[s.handle] = 1;
      });
    });
    var hs = Object.keys(need); if (!hs.length) return;
    var done = 0, filled = false;
    hs.forEach(function (h) {
      getProduct(h).then(function (p) {
        var u = p.featured_image || (p.images && p.images[0]) || '';
        if (u) { swImg[h] = u; filled = true; }
      }).catch(function () {}).then(function () {
        if (++done === hs.length && filled) schedule();
      });
    });
  }

  /* ==================================================================
     11. DRAWER CHROME  (band / totals / footer, via Section Rendering API)
     ================================================================== */
  function refreshChrome() {
    // cache:'no-store' — a bfcache/CDN hit used to return a stale free-shipping band.
    // .ok check — a failure used to be swallowed by an empty .catch, leaving the UI
    // showing the previous cart with nobody any the wiser.
    return fetch(location.pathname + '?sections=cart-drawer', { cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('section ' + r.status); return r.json(); })
      .then(function (j) {
        var html = new DOMParser().parseFromString(j['cart-drawer'] || '', 'text/html');
        ['.drawer__footer', '.free-shipping-threshold', '.totals'].forEach(function (sel) {
          var fresh = html.querySelector(sel), cur = document.querySelector('cart-drawer ' + sel);
          if (fresh && cur) cur.replaceWith(fresh);
        });
        document.dispatchEvent(new CustomEvent('vd:band:refreshed'));
        if (giftNoticePending != null) {
          var away = Math.max(0, drawerCfg().giftCents - giftNoticePending);
          var noticeMsg = 'Mystery gift removed — add ' + money(away) + ' to get it back';
          announce(noticeMsg);
          var band = document.querySelector('cart-drawer .vd-drawer__ship');
          if (band && !band.querySelector('.vd-ship__note')) {
            var n = document.createElement('p'); n.className = 'vd-ship__note';
            n.textContent = noticeMsg;
            band.appendChild(n);
            setTimeout(function () { if (n.parentNode) n.parentNode.removeChild(n); }, 6000);
          }
          giftNoticePending = null;
        }
      }).catch(function (e) {
        emit('error', { where: 'refreshChrome', message: (e && e.message) || '' });
      });
  }

  var deb;
  function schedule() { clearTimeout(deb); deb = setTimeout(function () { if (document.querySelector('cart-drawer-items')) refresh(); }, 120); }

  /* ==================================================================
     12. OPEN / CHECKOUT TELEMETRY
     ================================================================== */
  var wasOpen = false;
  function checkOpen() {
    if (!cdEl) return;
    // <cart-drawer> is position:fixed, and offsetParent is ALWAYS null for fixed elements —
    // the old `offsetParent !== null` test could never be true, so vd_cart_open never fired
    // and the primary metric had no denominator. Use the theme's own definition of "open"
    // (sections/cart-drawer.liquid open(): d.hidden=false + classList.add('is-open')).
    var open = !cdEl.hidden && cdEl.classList.contains('is-open');
    if (open === wasOpen) return;
    wasOpen = open;
    if (open) {
      drawerEverOpened = true;
      getCart().then(function (c) { emit('open', { item_count: c.item_count, total_price: c.total_price }); })
        .catch(function () { emit('open', {}); });
    } else {
      emit('close', {});
    }
  }
  // The footer (and therefore the Checkout button) is replaced on every cart write,
  // so this has to be delegated rather than bound to the element.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var b = t.closest('cart-drawer [name="checkout"], cart-drawer .cart__checkout-button, cart-drawer a[href*="/checkout"], cart-drawer button[href*="/checkout"]');
    if (!b) return;
    getCart().then(function (c) {
      emit('checkout_click', { item_count: c.item_count, total_price: c.total_price });
    }).catch(function () { emit('checkout_click', {}); });
  }, true);

  /* ==================================================================
     13. INIT
     ================================================================== */
  // Intelligems can finish loading AFTER this deferred script. If it does, and the shopper
  // has not opened the drawer yet, adopt the assigned group and re-render. Once the drawer
  // has been seen, the arm is frozen for the session — a shopper must never switch mid-visit.
  function adoptIgGroup() {
    if (wasOpen || drawerEverOpened) return;
    var v = String(window.VD_CD_VARIANT || '').toLowerCase();
    if (v !== 'control' && v !== 'gift') return;
    if (v === VARIANT) return;
    VARIANT = null;
    resolveVariant();
    recSig = -1;                                  // arm changed -> rail cache is stale
    if (document.querySelector('cart-drawer-items')) refresh();
  }
  try {
    window.addEventListener('ig:ready', adoptIgGroup);
    var prevReady = window.onIgReady;
    window.onIgReady = [].concat(prevReady || [], adoptIgGroup);
  } catch (e) {}

  function init() {
    resolveVariant();
    injectCSS();
    cdEl = document.querySelector('cart-drawer');
    // Measure the mis-assignment window instead of assuming it away: if Intelligems has not
    // finished loading when the drawer initialises, this session started on the fallback arm.
    // ig_ready=false rate IS the contamination rate — read it off the events, don't guess.
    emit('init', {
      ig_ready: !!(window.igData && window.igData.user),
      ig_forced: !!window.VD_CD_VARIANT
    });

    if (cdEl && window.MutationObserver) {
      mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var t = muts[i].target;
          if (t && t.closest && (t.closest('.vd-cd') || t.closest('.vd-cd-sheet-root'))) continue;   // ignore our own subtrees (rows/rail + quick-add sheet)
          schedule(); return;
        }
      });
      observeOn();
      // separate observer for open/close — attribute-only, never triggers a re-render
      new MutationObserver(checkOpen).observe(cdEl, { attributes: true, attributeFilter: ['class', 'hidden', 'open', 'style', 'aria-hidden'] });
      new MutationObserver(checkOpen).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      checkOpen();
    }

    if (document.querySelector('cart-drawer-items')) {
      refresh();
      // Grouptify (window.mpg) can load AFTER this first render, and merely opening the
      // drawer does not re-render — so poll briefly and re-render once it arrives.
      if (!(window.mpg && window.mpg.groups)) {
        var mpgTries = 0;
        var mpgTimer = setInterval(function () {
          if (window.mpg && window.mpg.groups) {
            clearInterval(mpgTimer);
            if (document.querySelector('cart-drawer-items')) refresh();
          } else if (++mpgTries > 40) clearInterval(mpgTimer);   // give up after ~10s
        }, 250);
      }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
